import { getSearchParams } from "../../../utils/request";
import { clearClientAuthState } from "../../../utils/socketAuth";
import { api } from "../../api";

const accountApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentUser: builder.query({
      query: () => "account/current",
      providesTags: ["CurrentUser"],
      transformResponse: (response) => {
        return response.data;
      },
    }),
    updateAccount: builder.mutation({
      query(data) {
        return {
          url: "account/update",
          method: "PUT",
          body: { user: data },
        };
      },
      invalidatesTags: ["CurrentUser"],
    }),
    /** Start email change — sends code to the new address only. */
    requestEmailChange: builder.mutation({
      query: (body) => ({
        url: "account/email-change/request",
        method: "POST",
        body,
      }),
      invalidatesTags: ["CurrentUser"],
    }),
    verifyEmailChange: builder.mutation({
      query: (body) => ({
        url: "account/email-change/verify",
        method: "POST",
        body,
      }),
      invalidatesTags: ["CurrentUser"],
    }),
    cancelEmailChange: builder.mutation({
      query: () => ({
        url: "account/email-change/cancel",
        method: "POST",
      }),
      invalidatesTags: ["CurrentUser"],
    }),
    resendEmailChange: builder.mutation({
      query: (body = {}) => ({
        url: "account/email-change/resend",
        method: "POST",
        body,
      }),
      invalidatesTags: ["CurrentUser"],
    }),
    signin: builder.mutation({
      query: (credentials) => ({
        url: "/account/login",
        method: "POST",
        body: credentials,
        credentials: "include",
      }),
      invalidatesTags: ["Auth", "User", "Config", "CurrentUser"],
    }),
    currentUser: builder.query({
      query: () => ({
        url: "/account/current",
        method: "GET",
      }),
      // Keep AuthProvider's user context in sync when account is updated.
      providesTags: ["CurrentUser"],
    }),
    refresh: builder.mutation({
      query: ({ refreshToken }) => ({
        url: "/account/refresh",
        method: "POST",
        body: { refreshToken },
      }),
    }),
    logout: builder.mutation({
      query: () => ({
        url: "/account/logout",
        method: "POST",
        credentials: "include",
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          console.error("Logout error:", error);
        } finally {
          clearClientAuthState();
        }
      },
    }),
    forgotPassword: builder.mutation({
      query(data) {
        return {
          url: "account/forgot/password",
          method: "POST",
          body: data,
        };
      },
    }),
    resetPassword: builder.mutation({
      query(data) {
        return {
          url: "account/reset/password",
          method: "POST",
          body: data,
        };
      },
    }),
    changePassword: builder.mutation({
      query({ data, ...params }) {
        const query = getSearchParams(params);
        return {
          url: `account/change/password?${query}`,
          method: "POST",
          body: data,
        };
      },
    }),
    verifyLoginMfa: builder.mutation({
      query(data) {
        return {
          url: "account/login/mfa/verify",
          method: "POST",
          body: data,
          credentials: "include",
        };
      },
      invalidatesTags: ["Auth", "User", "Config", "CurrentUser"],
    }),
    resendLoginMfa: builder.mutation({
      query(data) {
        return {
          url: "account/login/mfa/resend",
          method: "POST",
          body: data,
        };
      },
    }),
    activateAccount: builder.mutation({
      query({ data, ...params }) {
        const query = getSearchParams(params);
        return {
          url: `account/activate?${query}`,
          method: "PUT",
          body: { user: data },
        };
      },
      invalidatesTags: ["CurrentUser"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCurrentUserQuery,
  useUpdateAccountMutation,
  useRequestEmailChangeMutation,
  useVerifyEmailChangeMutation,
  useCancelEmailChangeMutation,
  useResendEmailChangeMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useVerifyLoginMfaMutation,
  useResendLoginMfaMutation,
  useActivateAccountMutation,
  useSigninMutation,
  useCurrentUserQuery,
  useRefreshMutation,
  useLogoutMutation,
} = accountApi;
